import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { Input } from '@tempo-io/sui-input';
import { Textarea } from '@tempo-io/sui-textarea';
import type { ChangeEvent, HTMLAttributes } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const TextFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  onTextBlur,
  isEditableText,
  ...rest
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  onTextBlur: () => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
}) => {
  const { t } = useTranslation('translation');

  const [hasFocus, setHasFocus] = useState(false);

  const handleBlur = () => {
    setHasFocus(false);
    onTextBlur();
  };

  if (fieldConfiguration.fieldType !== 'TEXT') return null;

  const rows = fieldConfiguration.rows ?? 3;
  const multiline = fieldConfiguration.multiline ?? false;

  const onChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues([event.target.value]);
  };

  if (multiline) {
    return (
      <Textarea
        className="fs-exclude"
        data-dd-action-name="FIM 3 Custom Field - text field redacted"
        data-testid="fim3-custom-field-input-text-input-field"
        value={values[0] ?? ''}
        onChange={onChange}
        onFocus={() => setHasFocus(true)}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        rows={isEditableText && !hasFocus ? 1 : rows}
        placeholder={isEditableText ? '' : t(($) => $.intake.general.textFieldPlaceholder)}
        maxLength={fieldConfiguration.maxLength ?? undefined}
        {...(rest as HTMLAttributes<HTMLTextAreaElement>)}
      />
    );
  } else {
    return (
      <Input
        className="fs-exclude"
        data-dd-action-name="FIM 3 Custom Field - text field redacted"
        data-testid="fim3-custom-field-input-text-input-field"
        value={values[0] ?? ''}
        onChange={onChange}
        onFocus={() => {
          setHasFocus(true);
        }}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        placeholder={isEditableText ? '' : t(($) => $.intake.general.textFieldPlaceholder)}
        maxLength={fieldConfiguration.maxLength ?? undefined}
        {...rest}
      />
    );
  }
};
